import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as api from "./csaApi";
import CsaAssess from "./CsaAssess";
import CsaWriteUp from "./CsaWriteUp";
import CsaExtras from "./CsaExtras";
import CsaSubmit from "./CsaSubmit";
import CsaCameraRoll from "./CsaPhotos";
import CsaReport from "./CsaReport";
import CsaNotePad from "./CsaNotePad";
import { CsaSyncBadge, CsaOfflineButton } from "./CsaOffline";
import { CarryOffer, CarryBanner } from "./CsaCarry";
import "./CsaCapture.css";

/**
 * The CSA audit screen — route /csa-audit/:auditId (design §7f).
 *
 * One container holds the audit, because the two passes are two views of the
 * same work (§7e): mark on site, write up at the desk. Splitting them across
 * routes would reload 163 questions on every switch, which is exactly the wrong
 * thing on a tablet with no signal.
 *
 * Everything reaches the server through csaApi. Nothing here calls axios, so
 * the offline layer can land later without this file changing.
 */

const TABS = [
  { key: "assess", label: "Assessment" },
  { key: "writeup", label: "Write-up" },
  { key: "photos", label: "Photos" },
  { key: "extras", label: "Other observations" },
  // Report comes after Finish because it does not exist until submit freezes
  // the score — the tab order is the order of the work.
  { key: "submit", label: "Finish" },
  { key: "report", label: "Report" },
];

export default function CsaAudit() {
  const { auditId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [framework, setFramework] = useState(null);
  const [audit, setAudit] = useState(null);
  const [answers, setAnswers] = useState({});
  const [exclusions, setExclusions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [client, setClient] = useState(null);
  // Whether this viewer may sign this audit off. The server decides — see
  // canReviewAudit — and the screen is told, never works it out.
  const [canReview, setCanReview] = useState(false);
  /*
   * Whether the capture screen may offer the Tier 3 comment assist (§8).
   *
   * The server decides, because the switch is an environment variable the
   * browser cannot see. It is combined with the connection state further down
   * — a cached audit loaded offline still says true, and the assist needs a
   * network.
   */
  const [assistEnabled, setAssistEnabled] = useState(false);
  /*
   * Whether this viewer may take the audit back themselves. Not derivable
   * from canReview — that one refuses whoever prepared the audit and this one
   * requires it, so the server answers both separately.
   */
  const [canReopen, setCanReopen] = useState(false);
  const [photos, setPhotos] = useState([]);

  const [tab, setTab] = useState("assess");
  const [padOpen, setPadOpen] = useState(false);
  const [unusedNotes, setUnusedNotes] = useState(0);
  const [activeSub, setActiveSub] = useState(null);
  const [status, setStatus] = useState(null);

  /* carry-forward (§8) */
  const [carryOffer, setCarryOffer] = useState(null);
  const [carryBusy, setCarryBusy] = useState(false);
  const [previous, setPrevious] = useState(null);

  const rollupTimer = useRef(null);
  const statusTimer = useRef(null);

  /* ---------------- load ---------------- */

  useEffect(() => {
    let live = true;
    setLoading(true);
    Promise?.all([api?.getFramework(), api?.getAudit(auditId)])
      .then(([fw, data]) => {
        if (!live) return;
        setFramework(fw);
        setAudit(data?.audit);
        setCanReview(!!data?.can_review);
        setAssistEnabled(!!data?.assist_enabled);
        setCanReopen(!!data?.can_reopen);
        setClient(data?.organization);
        setAnswers(indexBy(data?.answers));
        setExclusions(data?.exclusions || []);
        setSummary(data?.summary || null);
        setLoadError(null);
      })
      .catch((e) => live && setLoadError(e.message))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [auditId]);

  /*
   * Two separate questions, asked in two places on purpose.
   *
   *   carry-preview  is there a previous visit, and would you like it?
   *   previous       what did that visit actually say, question by question?
   *
   * The first is asked once, for a draft nobody has started. The second is
   * asked whenever a prior audit is linked — including long after carrying,
   * because "what did we find last time" stays useful for the whole audit and
   * is the reason a repeat visit is worth anything.
   */
  const loadPrevious = useCallback(
    () =>
      api
        .previousAnswers(auditId)
        .then((r) => setPrevious((r && r.answers) || null))
        .catch(() => setPrevious(null)),
    [auditId]
  );

  useEffect(() => {
    if (!audit) return;
    if (audit.prior_audit_id) {
      loadPrevious();
      return;
    }
    // Never offer to carry into an audit that is finished, or one someone has
    // already started answering — the server refuses both, and offering a
    // choice the server will reject is worse than not offering it.
    if (audit.status !== "draft") return;
    let live = true;
    api
      .carryPreview(auditId)
      .then((p) => live && p && p.available && setCarryOffer(p))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [audit, auditId, loadPrevious]);

  /*
   * Loaded once for the whole audit, not per question card. Every capture
   * screen reads from this one list.
   */
  const loadPhotos = useCallback(
    () => api.listPhotos(auditId).then(setPhotos).catch(() => setPhotos([])),
    [auditId]
  );

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  useEffect(() => {
    api
      .listNotes(auditId)
      .then((rows) => setUnusedNotes((rows || []).filter((n) => !n.used_for).length))
      .catch(() => {});
  }, [auditId]);

  useEffect(
    () => () => {
      clearTimeout(rollupTimer.current);
      clearTimeout(statusTimer.current);
    },
    []
  );

  /**
   * Re-read the audit after something changed its status.
   *
   * Signing off or returning changes what every tab may do — a returned audit
   * becomes editable again — so this refreshes the answers and the score too,
   * not only the status.
   */
  const reload = useCallback(
    () =>
      api
        .getAudit(auditId)
        .then((d) => {
          setAudit(d.audit);
          setCanReview(!!d.can_review);
          setAssistEnabled(!!d.assist_enabled);
          setCanReopen(!!d.can_reopen);
          setAnswers(indexBy(d.answers));
          setExclusions(d.exclusions || []);
          setSummary(d.summary || null);
        })
        .catch(() => {}),
    [auditId]
  );

  /* ---------------- derived ---------------- */

  const readOnly = !audit || audit.status !== "draft";

  /** Sub-pillar name -> the exclusion covering it, if any. */
  const excludedSubs = useMemo(() => {
    const m = {};
    exclusions.forEach((e) => {
      m[e.level_value] = e;
    });
    return m;
  }, [exclusions]);

  const subPillarNames = useMemo(() => {
    if (!framework) return [];
    const out = [];
    framework?.pillars?.forEach((p) => p?.sub_pillars?.forEach((s) => out?.push(s?.name)));
    return out;
  }, [framework]);

  /*
   * Answered counts come from local state, not the server. They change on
   * every tap, and a round trip per tap would make the rail lag behind the
   * auditor's thumb. Conformance percentages still come from the server —
   * those depend on the scoring config and must not be guessed at here.
   */
  const counts = useMemo(() => {
    const bySub = {};
    const byPillar = {};
    if (!framework) return { bySub, byPillar };
    framework?.pillars?.forEach((p) => {
      byPillar[p?.pillar_no] = { done: 0, total: 0 };
      p?.sub_pillars?.forEach((s) => {
        let done = 0;
        let total = 0;
        s?.elements?.forEach((el) =>
          el?.questions?.forEach((q) => {
            total += 1;
            if (answers[q.code] && answers[q.code].answer) done += 1;
          })
        );
        bySub[s.name] = { done, total };
        byPillar[p.pillar_no].done += done;
        byPillar[p.pillar_no].total += total;
      });
    });
    return { bySub, byPillar };
  }, [framework, answers]);

  /*
   * The gate's own number. Counted from the answers rather than kept as a
   * tally, so it cannot drift out of step with what is on screen after a
   * confirm, an edit, or a re-read.
   */
  const carriedOpen = useMemo(() => {
    let open = 0;
    let total = 0;
    Object.values(answers).forEach((a) => {
      if (!a || !a.carried_from_audit_id) return;
      total += 1;
      if (!a.is_confirmed) open += 1;
    });
    return { open, total };
  }, [answers]);

  const totals = useMemo(() => {
    let done = 0;
    let total = 0;
    Object.values(counts.byPillar).forEach((c) => {
      done += c.done;
      total += c.total;
    });

    // Scoped-out questions are answered but not assessed — they leave both
    // sides of the score, so the header must not count them as progress.
    const excluded = Object.values(answers).filter((a) => a && a.na_source).length;

    return { done: done - excluded, excluded, inScope: total - excluded, total };
  }, [counts, answers]);

  // Land on the first sub-pillar that still has work, not always on P1.
  useEffect(() => {
    if (activeSub || !framework) return;
    for (const p of framework?.pillars) {
      for (const s of p?.sub_pillars) {
        const c = counts?.bySub[s?.name];
        if (c && c?.done < c?.total) {
          setActiveSub(s?.name);
          return;
        }
      }
    }
    setActiveSub(framework?.pillars[0]?.sub_pillars[0].name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framework]);

  /** Take the auditor to the first carried answer nobody has looked at. */
  const jumpToFirstCarry = useCallback(() => {
    if (!framework) return;
    for (const p of framework.pillars) {
      for (const s of p.sub_pillars) {
        for (const el of s.elements) {
          for (const q of el.questions) {
            const a = answers[q.code];
            if (a && a.carried_from_audit_id && !a.is_confirmed) {
              setActiveSub(s.name);
              setTab("assess");
              setTimeout(() => {
                const node = document.getElementById("q-" + q.code);
                if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
              }, 60);
              return;
            }
          }
        }
      }
    }
  }, [framework, answers]);

  /* ---------------- status line ---------------- */

  const flash = useCallback((kind, text, ms) => {
    clearTimeout(statusTimer.current);
    setStatus({ kind, text });
    if (ms) statusTimer.current = setTimeout(() => setStatus(null), ms);
  }, []);

  /** Scores are the server's business — ask it, but not on every keystroke. */
  const scheduleRollup = useCallback(() => {
    clearTimeout(rollupTimer.current);
    rollupTimer.current = setTimeout(() => {
      api
        .getRollup(auditId)
        .then(setSummary)
        .catch(() => {});
    }, 2500);
  }, [auditId]);

  /* ---------------- writes ---------------- */

  const write = useCallback(
    async (code, patch) => {
      const before = answers[code];
      if (!before) return;

      // Optimistic: the button must colour under the thumb, not after a round
      // trip. A failure below puts the old value back.
      setAnswers((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));
      flash("saving", "Saving…");

      try {
        const row = await api.saveAnswer(auditId, {
          question_code: code,
          row_version: before.row_version,
          ...patch,
        });
        setAnswers((prev) => ({ ...prev, [code]: row }));
        flash("saved", "Saved", 1200);
        scheduleRollup();
      } catch (e) {
        if (e.isConflict && e.body && e.body.current) {
          // Someone else already changed this. Show theirs rather than
          // overwrite it — never a silent last-writer-wins (design §6).
          setAnswers((prev) => ({
            ...prev,
            [code]: { ...prev[code], ...e.body.current },
          }));
          flash("error", `${code} was changed elsewhere — showing the saved version`, 6000);
        } else {
          setAnswers((prev) => ({ ...prev, [code]: before }));
          flash("error", e.message, 6000);
        }
      }
    },
    [answers, auditId, flash, scheduleRollup]
  );

  const onAnswer = useCallback(
    (code, value) => write(code, { answer: value, set_by_bulk: false }),
    [write]
  );

  /*
   * The third argument records that these are the assist words, accepted,
   * rather than the auditor own. It is cleared by every ordinary edit, which
   * is what makes it mean something to a reviewer later.
   */
  const onComment = useCallback(
    (code, comment, assisted) =>
      write(code, { comment, comment_assisted: !!assisted }),
    [write]
  );

  /** Answer by exception — fill the blanks in one sub-pillar with Yes (§7e). */
  const onBulkYes = useCallback(
    async (subName) => {
      const sub = findSub(framework, subName);
      if (!sub) return;
      flash("saving", "Setting the remaining answers…");
      try {
        const r = await api.bulkYes(auditId, "sub_pillar", subName);
        /*
         * Applied locally rather than re-fetched. The server's rule is exactly
         * "every row still null becomes yes", so the same rows change here and
         * the result is identical — and it stays identical once this call is
         * queued offline. Any drift surfaces as a 409 on the next edit, which
         * is already handled.
         */
        setAnswers((prev) => {
          const next = { ...prev };
          sub.elements.forEach((el) =>
            el.questions.forEach((q) => {
              const cur = next[q.code];
              if (cur && !cur.answer) {
                next[q.code] = {
                  ...cur,
                  answer: "yes",
                  set_by_bulk: true,
                  row_version: cur.row_version + 1,
                };
              }
            })
          );
          return next;
        });
        flash("saved", `${r.answered} set to Yes · ${r.left_untouched} left as answered`, 3000);
        scheduleRollup();
      } catch (e) {
        flash("error", e.message, 6000);
      }
    },
    [framework, auditId, flash, scheduleRollup]
  );

  /* ---------------- carry-forward (§8) ---------------- */

  const onCarry = useCallback(async () => {
    setCarryBusy(true);
    try {
      const r = await api.carryForward(auditId);
      // Re-read rather than patch locally. Carrying rewrites 163 rows and
      // their row_versions; guessing at them here would hand the next edit a
      // stale version and a 409 the auditor did nothing to deserve.
      const data = await api.getAudit(auditId);
      setAudit(data.audit);
      setAnswers(indexBy(data.answers));
      setExclusions(data.exclusions || []);
      setSummary(data.summary || null);
      setCarryOffer(null);
      await loadPrevious();
      flash(
        "saved",
        `${r.carried} answers carried across. All ${r.needs_confirming} still need checking.`,
        6000
      );
    } catch (e) {
      flash("error", e.message, 6000);
    } finally {
      setCarryBusy(false);
    }
  }, [auditId, flash, loadPrevious]);

  /** Declining is a real choice, and leaves no trace on the audit. */
  const onStartFresh = useCallback(() => {
    setCarryOffer(null);
    flash("saved", "Starting fresh. The previous visit is still on file.", 3500);
  }, [flash]);

  const confirmCodes = useCallback(
    async (body, describe) => {
      setCarryBusy(true);
      try {
        const r = await api.confirmCarried(auditId, body);
        const data = await api.getAudit(auditId);
        setAnswers(indexBy(data.answers));
        flash("saved", describe(r), 3500);
      } catch (e) {
        flash("error", e.message, 6000);
      } finally {
        setCarryBusy(false);
      }
    },
    [auditId, flash]
  );

  const onConfirm = useCallback(
    (code) =>
      confirmCodes({ question_codes: [code] }, (r) =>
        r.still_unconfirmed
          ? `Checked. ${r.still_unconfirmed} left.`
          : "Checked — that was the last one."
      ),
    [confirmCodes]
  );

  const onConfirmAll = useCallback(
    () =>
      confirmCodes({ all: true }, (r) => `${r.confirmed} confirmed as they stand.`),
    [confirmCodes]
  );

  /* ---------------- scope ---------------- */

  const onExclude = useCallback(
    async (level, level_value, justification) => {
      try {
        const r = await api.addExclusion(auditId, { level, level_value, justification });
        setExclusions((prev) => [...prev, r.exclusion]);
        const data = await api.getAudit(auditId);
        setAnswers(indexBy(data.answers));
        setSummary(data.summary || null);
        flash(
          "saved",
          r.left_untouched.length
            ? `${r.marked_na} marked N/A · ${r.left_untouched.length} kept their answer`
            : `${r.marked_na} marked N/A`,
          4000
        );
      } catch (e) {
        flash("error", e.message, 6000);
      }
    },
    [auditId, flash]
  );

  const onRestoreScope = useCallback(
    async (exclusionId) => {
      try {
        await api.removeExclusion(auditId, exclusionId);
        setExclusions((prev) => prev.filter((e) => e.id !== exclusionId));
        const data = await api.getAudit(auditId);
        setAnswers(indexBy(data.answers));
        setSummary(data.summary || null);
        flash("saved", "Back in scope", 2500);
      } catch (e) {
        flash("error", e.message, 6000);
      }
    },
    [auditId, flash]
  );

  /* ---------------- render ---------------- */

  if (loading) {
    return (
      <div className="csa-capture csa-screen">
        <div className="cap-loading">Loading the audit…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="csa-capture csa-screen">
        <div className="cap-loading">
          <p>{loadError}</p>
          <button type="button" className="cap-btn" onClick={() => navigate("/csa-audits")}>
            Back
          </button>
        </div>
      </div>
    );
  }

  const overall = summary && summary.overall ? summary.overall : null;

  return (
    <div className="csa-capture csa-screen">
      <header className="cap-head">
        {/*
          An explicit way out. "Done for now" reads as finishing something;
          this is just leaving, which is what a browser back button would do
          if this screen were reachable any other way.
        */}
        <button
          type="button"
          className="cap-back"
          title="Back to your audits"
          onClick={() => navigate("/csa-audits")}
        >
          ‹
        </button>
        <div>
          <h1>{(client && client.org_name) || "CSA audit"}</h1>
          <div className="sub">
            {audit?.site_name || "—"} · {audit?.report_id}
            {readOnly ? ` · ${audit?.status}` : ""}
          </div>
        </div>

        <div className="cap-spacer" />

        {/*
          The percentage and the progress are read together, never apart.
          Conformance counts only answered questions, so one Yes out of 163
          reads as "100%" — true, and badly misleading on its own. The prototype
          shows them on one line for exactly this reason.
        */}
        <div className="cap-progress">
          <span className="cap-pct">
            {!overall || overall.pct == null ? "—" : `${overall.pct}%`}
          </span>
          <span className="cap-band">{(overall && overall.band) || "Not started"}</span>
          <span className="cap-prog">
            {totals.done} assessed
            {totals.excluded ? ` · ${totals.excluded} scoped out` : ""}
            {" · "}
            {totals.inScope} in scope of {totals.total}
          </span>
        </div>

        <CsaSyncBadge auditId={auditId} />

        <button type="button" className="cap-btn ghost" onClick={() => setPadOpen(true)}>
          Note pad{unusedNotes ? ` · ${unusedNotes}` : ""}
        </button>

        <button type="button" className="cap-btn" onClick={() => navigate("/csa-audits")}>
          Done for now
        </button>
      </header>

      <nav className="cap-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={"cap-tab" + (tab === t.key ? " on" : "")}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "assess" ? (
        <>
        <div className="cap-offline-slot">
          <CsaOfflineButton auditId={auditId} />
        </div>
        {readOnly ? null : (
          <CarryBanner
            outstanding={carriedOpen.open}
            total={carriedOpen.total}
            busy={carryBusy}
            onReview={jumpToFirstCarry}
            onConfirmAll={onConfirmAll}
          />
        )}
        <CsaAssess
          framework={framework}
          auditId={auditId}
          photos={photos}
          onPhotosChanged={loadPhotos}
          answers={answers}
          counts={counts}
          summary={summary}
          excludedSubs={excludedSubs}
          activeSub={activeSub}
          setActiveSub={setActiveSub}
          readOnly={readOnly}
          previous={previous}
          onConfirm={onConfirm}
          onAnswer={onAnswer}
          onComment={onComment}
          assistEnabled={assistEnabled}
          onBulkYes={onBulkYes}
          onExclude={onExclude}
          onRestoreScope={onRestoreScope}
        />
        </>
      ) : null}

      {tab === "writeup" ? (
        <CsaWriteUp
          auditId={auditId}
          readOnly={readOnly}
          onComment={onComment}
          onNotesChanged={setUnusedNotes}
          onJump={(code, subPillar) => {
            // Called with no arguments from the empty state — that just means
            // "take me back", with no particular question to land on.
            if (subPillar) setActiveSub(subPillar);
            setTab("assess");
            if (!code) return;
            setTimeout(() => {
              const el = document.getElementById("q-" + code);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 60);
          }}
        />
      ) : null}

      {tab === "photos" ? (
        <CsaCameraRoll
          auditId={auditId}
          framework={framework}
          readOnly={readOnly}
          onChange={loadPhotos}
        />
      ) : null}

      {tab === "report" ? <CsaReport auditId={auditId} audit={audit} /> : null}

      {tab === "extras" ? (
        <CsaExtras auditId={auditId} readOnly={readOnly} framework={framework} />
      ) : null}

      {tab === "submit" ? (
        <CsaSubmit
          auditId={auditId}
          audit={audit}
          canReview={canReview}
          canReopen={canReopen}
          onSubmitted={() => reload()}
          onChanged={() => reload()}
        />
      ) : null}

      {status ? <div className={"cap-status " + status.kind}>{status.text}</div> : null}

      {carryOffer ? (
        <CarryOffer
          preview={carryOffer}
          busy={carryBusy}
          onCarry={onCarry}
          onStartFresh={onStartFresh}
        />
      ) : null}

      <CsaNotePad
        auditId={auditId}
        open={padOpen}
        onClose={() => setPadOpen(false)}
        subPillars={subPillarNames}
        currentSubPillar={tab === "assess" ? activeSub : null}
        onCountChange={setUnusedNotes}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function indexBy(rows) {
  const m = {};
  (rows || []).forEach((r) => {
    m[r.question_code] = r;
  });
  return m;
}

function findSub(framework, name) {
  if (!framework) return null;
  for (const p of framework.pillars) {
    const s = p.sub_pillars.find((x) => x.name === name);
    if (s) return s;
  }
  return null;
}
